// PricingCalculator.jsx — Sprint-82 pincode-only landing-page widget
// ----------------------------------------------------------------------
// Two-input flow: pickup pincode + delivery pincode + optional weight.
// Hits POST /api/pricing/estimate-by-pincode which:
//   1. validates both pincodes
//   2. runs the pickup-serviceability gate
//   3. delegates to the standard pricing engine
//
// States: idle / loading / success / unserviceable / validation-error.
// Reuses NotifyMeModal (existing) for the "Notify me" CTA when pickup
// pincode is outside any hub's radius.
import { useState, useRef } from "react";
import { MapPin, Loader2, IndianRupee, Tag, Truck, Weight, Clock, ArrowRight, Sparkles, AlertTriangle, BellRing } from "lucide-react";
import api from "../services/api";
import NotifyMeModal from "./NotifyMeModal";
import useStateServiceability from "../hooks/useStateServiceability";
import StateServiceabilityBanner from "./StateServiceabilityBanner";

const PINCODE_RE = /^[1-9][0-9]{5}$/;

const ZONE_COLORS = {
  A: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  B: { bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200" },
  C: { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200" },
  D: { bg: "bg-red-50",     text: "text-red-700",     border: "border-red-200" },
};

export default function PricingCalculator({ onGetStarted }) {
  const [pickupPin, setPickupPin] = useState("");
  const [deliveryPin, setDeliveryPin] = useState("");
  const [weight, setWeight] = useState("");
  const [errors, setErrors] = useState({ pickup: "", delivery: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [unserviceable, setUnserviceable] = useState(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const resultRef = useRef(null);

  // Sprint-101b — state-level live check on the pickup pincode.
  const stateSv = useStateServiceability({ pickupPin: pickupPin, pincode: pickupPin });

  const validate = () => {
    const e = { pickup: "", delivery: "" };
    if (!PINCODE_RE.test(pickupPin.trim())) e.pickup = "Enter a valid 6-digit pincode (e.g. 560038).";
    if (!PINCODE_RE.test(deliveryPin.trim())) e.delivery = "Enter a valid 6-digit pincode (e.g. 110001).";
    setErrors(e);
    return !e.pickup && !e.delivery;
  };

  const handleCalculate = async () => {
    setResult(null);
    setUnserviceable(null);
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/pricing/estimate-by-pincode", {
        pickup_pincode: pickupPin.trim(),
        delivery_pincode: deliveryPin.trim(),
        weight: weight ? parseFloat(weight) : 1.0,
        item_size: "small",
        delivery_speed: "express",
      });
      setResult(data);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Serviceability rejection → show red card + Notify Me CTA.
      if (detail?.code === "PICKUP_NOT_SERVICEABLE") {
        setUnserviceable({
          pickup_pincode: detail.pickup_pincode || pickupPin.trim(),
          nearest_hub: detail.nearest_hub || null,
          message: detail.message,
        });
      } else if (detail?.code === "INVALID_PICKUP_PIN") {
        setErrors((e) => ({ ...e, pickup: detail.message }));
      } else if (detail?.code === "INVALID_DELIVERY_PIN") {
        setErrors((e) => ({ ...e, delivery: detail.message }));
      } else {
        const status = err?.response?.status || "";
        const msg = typeof detail === "string" ? detail : (detail?.message || err?.message || "Could not calculate price");
        setErrors((e) => ({ ...e, pickup: `${status ? `HTTP ${status}: ` : ""}${msg}` }));
      }
    } finally {
      setLoading(false);
    }
  };

  const hasDiscounts = result?.discounts_applied?.length > 0;
  const totalDiscount = result?.total_discount || 0;
  const zc = ZONE_COLORS[result?.zone] || ZONE_COLORS.A;

  return (
    <div className="w-full" data-testid="pricing-calculator">
      <div className="grid lg:grid-cols-2 gap-8 items-start">
        {/* Left: input form */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="calculator-form">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#FF6B00] rounded-xl flex items-center justify-center shadow-sm shadow-[#FF6B00]/20">
              <IndianRupee className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-['Outfit'] text-lg font-bold text-slate-900">Instant Price Estimate</h3>
              <p className="text-xs text-slate-400">Just two pincodes — see your price in seconds.</p>
            </div>
          </div>

          {/* Pickup pincode */}
          <div className="mb-4">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Pickup Pincode</label>
            <div className="relative">
              <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="e.g. 560038"
                value={pickupPin}
                onChange={(e) => { setPickupPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setErrors((s) => ({ ...s, pickup: "" })); }}
                className={`w-full h-11 pl-10 pr-4 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all ${
                  errors.pickup ? "border-red-300 focus:border-red-400 focus:ring-red-100" : "border-slate-200 focus:border-[#FF6B00] focus:ring-[#FF6B00]/10"
                }`}
                data-testid="calc-pickup-pincode-input"
              />
            </div>
            {errors.pickup && <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1" data-testid="calc-pickup-error"><AlertTriangle className="h-3 w-3" /> {errors.pickup}</p>}
          </div>

          {/* Delivery pincode */}
          <div className="mb-4">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Delivery Pincode</label>
            <div className="relative">
              <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-red-400" />
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="e.g. 110001"
                value={deliveryPin}
                onChange={(e) => { setDeliveryPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setErrors((s) => ({ ...s, delivery: "" })); }}
                className={`w-full h-11 pl-10 pr-4 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all ${
                  errors.delivery ? "border-red-300 focus:border-red-400 focus:ring-red-100" : "border-slate-200 focus:border-[#FF6B00] focus:ring-[#FF6B00]/10"
                }`}
                data-testid="calc-delivery-pincode-input"
              />
            </div>
            {errors.delivery && <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1" data-testid="calc-delivery-error"><AlertTriangle className="h-3 w-3" /> {errors.delivery}</p>}
          </div>

          {/* Optional weight */}
          <div className="mb-5">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Weight (kg) <span className="text-slate-300 normal-case font-normal">— optional</span></label>
            <div className="relative">
              <Weight className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="number"
                step="0.1"
                min="0"
                placeholder="Default 1 kg"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/10 transition-all"
                data-testid="calc-weight-input"
              />
            </div>
          </div>

          {/* Sprint-101b — state-level live serviceability banner */}
          <StateServiceabilityBanner
            sv={stateSv}
            pincode={pickupPin}
            source="landing"
            className="mb-1"
          />

          <button
            onClick={handleCalculate}
            disabled={loading || stateSv?.serviceable === false}
            title={stateSv?.serviceable === false ? "Pickup not available in this state" : undefined}
            className="w-full h-12 bg-[#FF6B00] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#E65A00] transition-all shadow-sm shadow-[#FF6B00]/20 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="calc-submit-btn"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Sparkles className="h-4 w-4" /> Calculate Price</>}
          </button>
        </div>

        {/* Right: result / unserviceable / placeholder */}
        <div ref={resultRef} className="lg:sticky lg:top-24">
          {!result && !loading && !unserviceable && (
            <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-200 p-10 flex flex-col items-center justify-center text-center min-h-[420px]" data-testid="calc-placeholder">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                <Truck className="h-8 w-8 text-slate-300" />
              </div>
              <p className="text-sm font-medium text-slate-400 mb-1">Your estimate will appear here</p>
              <p className="text-xs text-slate-300">Enter two pincodes and click Calculate</p>
            </div>
          )}

          {loading && (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 flex flex-col items-center justify-center text-center min-h-[420px] shadow-sm animate-pulse">
              <Loader2 className="h-10 w-10 text-[#FF6B00] animate-spin mb-4" />
              <p className="text-sm font-medium text-slate-500">Checking serviceability & pricing…</p>
            </div>
          )}

          {unserviceable && !loading && stateSv?.serviceable !== true && (
            <div className="bg-red-50 rounded-2xl border border-red-200 p-8 min-h-[420px] flex flex-col" data-testid="calc-unserviceable-card">
              <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mb-4">
                <AlertTriangle className="h-7 w-7 text-red-500" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-bold text-red-900 mb-2">Pickup not available in this area</h3>
              <p className="text-sm text-red-800/80 mb-4 leading-relaxed">{unserviceable.message}</p>
              {unserviceable.nearest_hub?.name && (
                <p className="text-xs text-slate-600 bg-white rounded-lg border border-red-100 px-3 py-2 mb-5" data-testid="calc-unserviceable-nearest">
                  <span className="font-semibold">Nearest hub:</span> {unserviceable.nearest_hub.name}
                  {unserviceable.nearest_hub.city ? `, ${unserviceable.nearest_hub.city}` : ""}
                  {unserviceable.nearest_hub.distance_km ? ` · ${unserviceable.nearest_hub.distance_km} km away` : ""}
                </p>
              )}
              <button
                onClick={() => setNotifyOpen(true)}
                className="mt-auto w-full h-11 bg-white text-red-700 border border-red-200 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-red-50 transition-all"
                data-testid="calc-notify-me-btn"
              >
                <BellRing className="h-4 w-4" /> Notify me when available
              </button>
            </div>
          )}

          {result && !loading && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden" data-testid="calc-result-card">
              <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6 py-5 text-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${zc.bg} ${zc.text} border ${zc.border}`}>
                      <MapPin className="h-2.5 w-2.5" />
                      Zone {result.zone}
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium">
                      {result.distance_source === "osrm" && "ROAD"} {result.distance_km} km
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-md text-[10px] font-bold">
                    <Clock className="h-2.5 w-2.5" />
                    ~{(result.estimated_delivery_days || result.estimated_days) === 1 ? "1 day" : `${result.estimated_delivery_days || result.estimated_days} days`}
                  </span>
                </div>
                <div className="flex items-end gap-2">
                  {totalDiscount > 0 && (
                    <span className="text-lg text-slate-500 line-through font-medium">INR {result.base_total}</span>
                  )}
                  <span className="text-4xl font-bold font-['Outfit'] tracking-tight" data-testid="calc-final-price">
                    INR {result.final_price ?? result.total}
                  </span>
                </div>
                {totalDiscount > 0 && (
                  <span className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-green-400 bg-green-500/15 px-2.5 py-1 rounded-lg" data-testid="calc-savings-badge">
                    <Tag className="h-3 w-3" /> You save INR {totalDiscount}
                  </span>
                )}
              </div>

              <div className="px-6 py-5 space-y-3">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Price Breakdown</p>
                <div className="space-y-2.5 text-xs">
                  <div className="flex justify-between text-slate-600">
                    <span className="flex items-center gap-1.5"><Weight className="h-3 w-3 text-slate-400" /> Weight Charges ({result.weight_slab})</span>
                    <span className="font-semibold text-slate-800">INR {result.weight_charge}</span>
                  </div>
                  {(result.base_price ?? 0) > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span className="flex items-center gap-1.5"><Truck className="h-3 w-3 text-slate-400" /> Service Charges</span>
                      <span className="font-semibold text-slate-800">INR {result.base_price}</span>
                    </div>
                  )}
                  {result.gst > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span className="flex items-center gap-1.5"><span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1 py-0.5 rounded">GST</span> Tax (18%)</span>
                      <span className="font-semibold text-slate-800">INR {result.gst}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-100 pt-2 flex justify-between text-slate-900 font-bold text-sm">
                    <span>Subtotal</span><span>INR {result.base_total}</span>
                  </div>
                </div>

                {hasDiscounts && (
                  <div className="bg-green-50/60 border border-green-100 rounded-xl px-4 py-3 space-y-2" data-testid="calc-discounts">
                    <p className="text-[10px] font-bold text-green-700 uppercase tracking-wider flex items-center gap-1"><Tag className="h-3 w-3" /> Discounts Applied</p>
                    {result.discounts_applied.map((d, i) => (
                      <div key={d.type || d.label || i} className="flex justify-between text-xs text-green-700">
                        <span>{d.label}</span><span className="font-bold">-INR {d.discount || 0}</span>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={onGetStarted}
                  className="w-full h-11 bg-[#FF6B00] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#E65A00] transition-all shadow-sm shadow-[#FF6B00]/20 mt-2"
                  data-testid="calc-get-started-btn"
                >
                  Book This Delivery <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {notifyOpen && (
        <NotifyMeModal
          open={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          prefill={{
            pickup_pincode: unserviceable?.pickup_pincode || pickupPin.trim(),
            requested_city: unserviceable?.nearest_hub?.city || "",
          }}
        />
      )}
    </div>
  );
}
